<div class="guided-course-creation-dialog">
  <mat-toolbar class="elearnio-mat-dialog-full-panel-class-toolbar">
    <div class="toolbar-content">
      <div class="elearnio-mat-dialog-full-panel-class-toolbar-header-title">
        <span class="headline-tertiary">{{
          'guidedCourseCreation.title' | translate
        }}</span>
      </div>
      <div class="elearnio-mat-dialog-full-panel-class-toolbar-header-actions">
        @if (currentPage() === 'form') {
          @if (member() && !termsAlreadyAccepted() && !isLoading()) {
            <div class="terms-checkbox">
              <mat-checkbox
                [checked]="member()?.aiTermsAgreedTo ?? false"
                (change)="onTermsAndConditionsChange($event)"
              ></mat-checkbox>
              <span
                [innerHTML]="
                  'reusable.acceptTermsOfUse'
                    | translate
                      : {
                          termsUrl:
                            'https://elearnio.com/de/ai-nutzungsbedingungen',
                        }
                "
              ></span>
            </div>
          }
          <button
            mat-stroked-button
            color="yellow"
            class="no-border filled"
            [disabled]="
              !isFormValid ||
              !member()?.aiTermsAgreedTo ||
              isLoading() ||
              isSessionLoading() ||
              isDocumentUploading()
            "
            (click)="onGenerate()"
          >
            {{ 'guidedCourseCreation.generateOutline' | translate }}
          </button>
        }

        @if (currentPage() === 'outline') {
          @if (!isSectionsLoading()) {
            <a class="back-button" (click)="onBack()">
              <mat-icon>chevron_left</mat-icon>
              {{ 'common.back' | translate }}
            </a>
          }
          @if (!isSectionsLoading() && !sectionsError()) {
            <button
              mat-stroked-button
              color="yellow"
              class="no-border filled"
              [disabled]="isLoading()"
              (click)="onCreateCourse()"
            >
              {{
                (contentGenerationEnabled()
                  ? 'guidedCourseCreation.outline.createCourseAndContent'
                  : 'main.profile.course.toolbar.createCourse'
                ) | translate
              }}
            </button>
          }
        }

        <button
          mat-stroked-button
          color="darkblue"
          class="close-btn no-padding small-icon-button"
          (click)="onClose()"
        >
          <mat-icon class="size-m">close</mat-icon>
        </button>
      </div>
    </div>
  </mat-toolbar>

  <div
    class="outline-page-layout"
    [class.outline-page-layout--active]="currentPage() === 'outline'"
  >
    <div class="guided-course-creation-dialog-scroll" #scrollContainer>
      <div class="guided-course-creation-dialog-body">
        @if (currentPage() === 'form') {
          <form [formGroup]="form" class="guided-course-creation-dialog-form">
            <!-- Topic -->
            <section class="form-section">
              <label class="form-section-label" for="gcc-topic">
                {{ 'guidedCourseCreation.topic.label' | translate }}
                <app-tooltip-button
                  [tooltipKey]="'tooltips.guidedCourseCreation.topic'"
                ></app-tooltip-button>
              </label>
              <div class="topic-field">
                <textarea
                  id="gcc-topic"
                  name="topic"
                  formControlName="topic"
                  class="topic-textarea"
                  [placeholder]="
                    'guidedCourseCreation.topic.placeholder' | translate
                  "
                ></textarea>
              </div>
            </section>

            <!-- Source document (optional) -->
            <section class="form-section">
              <app-document-upload
                [sessionId]="sessionId()"
                [disabled]="isSessionLoading()"
                [currentTopic]="form.controls.topic.value"
                (statusChange)="onDocumentStatusChange($event)"
                (topicSelected)="onTopicSuggestionSelected($event)"
              ></app-document-upload>
            </section>

            <!-- Language -->
            <section class="form-section">
              <label class="form-section-label">
                {{ 'guidedCourseCreation.language.label' | translate }}
                <app-tooltip-button
                  [tooltipKey]="'tooltips.guidedCourseCreation.language'"
                ></app-tooltip-button>
              </label>
              <mat-form-field appearance="outline" class="language-select">
                <mat-select formControlName="language">
                  <mat-select-trigger>
                    <app-elearnio-language-span
                      [countryCode]="form.controls.language.value"
                      [showCountryCode]="true"
                    ></app-elearnio-language-span>
                  </mat-select-trigger>
                  @for (lang of availableLanguages; track lang) {
                    <mat-option [value]="lang">
                      <app-elearnio-language-span
                        [countryCode]="lang"
                        [showCountryCode]="true"
                      ></app-elearnio-language-span>
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </section>

            <!-- Intent -->
            <section class="form-section">
              <label class="form-section-label">
                {{ 'guidedCourseCreation.intent.label' | translate }}
                <app-tooltip-button
                  [tooltipKey]="'tooltips.guidedCourseCreation.intent'"
                ></app-tooltip-button>
              </label>
              <mat-radio-group
                formControlName="intent"
                class="card-grid card-grid--3col"
              >
                @for (option of intentOptions; track option.id) {
                  <app-graphic-option-card
                    [title]="option.titleKey | translate"
                    [description]="option.descriptionKey | translate"
                    [icon]="option.icon"
                    [value]="option.id"
                    [selected]="form.controls.intent.value === option.id"
                    mode="radio"
                  />
                }
              </mat-radio-group>
            </section>

            <!-- Length type -->
            <section class="form-section">
              <label class="form-section-label">
                {{ 'guidedCourseCreation.lengthType.label' | translate }}
                <app-tooltip-button
                  [tooltipKey]="'tooltips.guidedCourseCreation.lengthType'"
                ></app-tooltip-button>
              </label>
              <mat-radio-group
                formControlName="lengthType"
                class="card-grid card-grid--3col"
              >
                @for (option of lengthTypeOptions; track option.id) {
                  <app-graphic-option-card
                    [title]="option.titleKey | translate"
                    [description]="option.descriptionKey | translate"
                    [icon]="option.icon"
                    [value]="option.id"
                    [selected]="form.controls.lengthType.value === option.id"
                    mode="radio"
                  />
                }
              </mat-radio-group>
            </section>

            <!-- Audience -->
            <section class="form-section">
              <label class="form-section-label">
                {{ 'guidedCourseCreation.audience.label' | translate }}
                <app-tooltip-button
                  [tooltipKey]="'tooltips.guidedCourseCreation.audience'"
                ></app-tooltip-button>
              </label>
              <div class="card-grid card-grid--3col">
                @for (option of audienceOptions; track option.id) {
                  <app-graphic-option-card
                    [title]="option.titleKey | translate"
                    [description]="option.descriptionKey | translate"
                    [icon]="option.icon"
                    [selected]="isAudienceSelected(option.id)"
                    (selectionChange)="toggleAudience(option.id)"
                    mode="checkbox"
                  />
                }
              </div>
            </section>
          </form>
        } @else {
          <!-- Phase 2+: Summary loaded -->
          <div class="course-outline">
            <!-- Summary (always visible once fetched) -->
            <div class="course-outline-header">
              <h2 class="headline-primary">{{ outline()?.title }}</h2>
              <app-tag
                variant="time"
                [label]="
                  (outline()?.totalTimeMinutes ?? '') +
                  ('common.time.minuteShort' | translate)
                "
              >
                <mat-icon class="size-s">schedule</mat-icon>
              </app-tag>
            </div>

            <div class="course-outline-summary">
              <div class="summary-row">
                <span class="summary-label">{{
                  'guidedCourseCreation.summary.language' | translate
                }}</span>
                <app-elearnio-language-span
                  [countryCode]="form.controls.language.value"
                  [showCountryCode]="true"
                ></app-elearnio-language-span>
              </div>
              <div class="summary-row">
                <span class="summary-label">{{
                  'guidedCourseCreation.summary.intent' | translate
                }}</span>
                <span>{{ formSummary().intentLabel }}</span>
              </div>
              <div class="summary-row">
                <span class="summary-label">{{
                  'guidedCourseCreation.summary.audience' | translate
                }}</span>
                <span>{{ formSummary().audienceLabel }}</span>
              </div>
            </div>

            <p class="course-outline-objectives">{{ learningObjectives() }}</p>

            <div class="course-outline-toggle-row">
              <h3 class="headline-secondary">
                {{ 'guidedCourseCreation.outline.courseOutline' | translate }}
              </h3>
              <app-ai-settings-panel
                [hasDocument]="hasDocument()"
                [contentMode]="effectiveContentMode()"
                [(contentGenerationEnabled)]="contentGenerationEnabled"
                [(imageGenerationEnabled)]="imageGenerationEnabled"
                (modeSelected)="contentModeOverride.set($event)"
              />
            </div>

            @if (outline()?.sections?.length) {
              <mat-accordion multi>
                @for (
                  section of outline()?.sections ?? [];
                  track $index;
                  let sectionIndex = $index
                ) {
                  <mat-expansion-panel expanded>
                    <mat-expansion-panel-header>
                      <mat-panel-title>
                        <h3>{{ sectionIndex + 1 }}. {{ section.headline }}</h3>
                      </mat-panel-title>
                    </mat-expansion-panel-header>
                    @if (section.modules.length) {
                      @for (
                        mod of section.modules;
                        track $index;
                        let moduleIndex = $index
                      ) {
                        <div class="module-tile">
                          <span class="module-tile-name"
                            >{{ sectionIndex + 1 }}.{{ moduleIndex + 1 }}.
                            {{ mod.name }}</span
                          >
                          <div class="module-tile-tags">
                            <app-tag [label]="mod.moduleType | translate">
                              <img [src]="mod.iconPath" alt="" />
                            </app-tag>
                            <div class="module-tile-tags-end">
                              @if (
                                contentGenerationEnabled() &&
                                isAiContentSupported(mod.rawModuleType)
                              ) {
                                <app-tag
                                  variant="aiContent"
                                  [label]="
                                    'guidedCourseCreation.outline.aiText'
                                      | translate
                                  "
                                >
                                  <mat-icon
                                    class="size-s"
                                    svgIcon="ai_icon"
                                  ></mat-icon>
                                </app-tag>
                              }
                              @if (
                                contentGenerationEnabled() &&
                                imageGenerationEnabled() &&
                                isAiImageSupported(mod.rawModuleType)
                              ) {
                                <app-tag
                                  variant="aiImage"
                                  [label]="
                                    'guidedCourseCreation.outline.aiImages'
                                      | translate
                                  "
                                >
                                  <mat-icon
                                    class="size-s"
                                    svgIcon="ai_icon"
                                  ></mat-icon>
                                </app-tag>
                              }
                              <app-tag
                                variant="time"
                                [label]="
                                  mod.estimatedTimeMinutes +
                                  ('common.time.minuteShort' | translate)
                                "
                              >
                                <mat-icon class="size-s">schedule</mat-icon>
                              </app-tag>
                            </div>
                          </div>
                          <label
                            class="module-tile-what-to-cover"
                            [for]="
                              'what-to-cover-' +
                              (sectionIndex + 1) +
                              '-' +
                              (moduleIndex + 1)
                            "
                          >
                            <strong>{{
                              'guidedCourseCreation.outline.whatToCover'
                                | translate
                            }}</strong
                            >:
                          </label>
                          <textarea
                            class="what-to-cover-field"
                            [id]="
                              'what-to-cover-' +
                              (sectionIndex + 1) +
                              '-' +
                              (moduleIndex + 1)
                            "
                            [value]="mod.whatToCover"
                            (blur)="
                              onWhatToCoverChange(sectionIndex, moduleIndex, $event)
                            "
                          ></textarea>
                        </div>
                      }
                    } @else if (isSectionsLoading()) {
                      <div class="course-section-loading">
                        <app-loading-spinner></app-loading-spinner>
                        <span>{{
                          'guidedCourseCreation.outline.working' | translate
                        }}</span>
                      </div>
                    }
                  </mat-expansion-panel>
                }
              </mat-accordion>
            } @else if (isSectionsLoading()) {
              <div class="course-section-loading">
                <app-loading-spinner></app-loading-spinner>
                <span>{{
                  'guidedCourseCreation.outline.working' | translate
                }}</span>
              </div>
            }
          </div>
        }
      </div>
    </div>

    @if (currentPage() === 'outline' && sessionId(); as sid) {
      <app-outline-chat
        [isLoading]="isLoading() || isSectionsLoading()"
        [sessionId]="sid"
        [sectionsLoaded]="!isSectionsLoading()"
        (outlineUpdated)="onOutlineUpdated($event)"
      />
    }
  </div>

  @if (isCreatingCourse()) {
    <app-creation-progress
      [progress]="creationProgress()"
      animate.enter="fade-enter"
    />
  }
</div>
